import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import { Input } from "@/ui/components/ui/input";
import { Button } from "@/ui/components/ui/button";
import { EyeIcon, EyeOffIcon } from "@/ui/components/icons";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import {
  resetPasswordSchema,
  type ResetPasswordFormData,
} from "@/schemas/passwordResetSchemas";

export interface ResetPasswordFormProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Email address for display in subtitle
   */
  email: string;
  /**
   * Callback when form is submitted
   */
  onSubmit: (data: ResetPasswordFormData) => Promise<void>;
  /**
   * Callback when resend code is clicked
   */
  onResendCode: () => Promise<void>;
  /**
   * Loading state during reset
   */
  isLoading?: boolean;
  /**
   * Whether resend code is available
   */
  canResend?: boolean;
  /**
   * Resend cooldown in seconds
   */
  resendCooldown?: number;
  /**
   * Global error message
   */
  error?: string | null;
}

/**
 * ResetPasswordForm Component
 *
 * Form for resetting password with verification code.
 * Includes password strength indicator and visibility toggles.
 * Follows LoginForm/SignupForm pattern with Zod validation.
 *
 * @example
 * ```tsx
 * <ResetPasswordForm
 *   email="user@example.com"
 *   onSubmit={async (data) => console.log(data)}
 *   onResendCode={async () => console.log('resend')}
 *   isLoading={false}
 *   canResend={true}
 *   resendCooldown={0}
 *   error={null}
 * />
 * ```
 */
export const ResetPasswordForm = React.forwardRef<
  HTMLFormElement,
  ResetPasswordFormProps
>(
  (
    {
      className,
      email,
      onSubmit,
      onResendCode,
      isLoading = false,
      canResend = true,
      resendCooldown = 0,
      error,
    },
    ref
  ) => {
    const { t } = useTranslation("auth");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const {
      register,
      handleSubmit,
      watch,
      formState: { errors },
    } = useForm<ResetPasswordFormData>({
      resolver: zodResolver(resetPasswordSchema),
    });

    const newPassword = watch("newPassword", "");

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const toggleConfirmPasswordVisibility = () => {
      setShowConfirmPassword((prev) => !prev);
    };

    return (
      <div
        className={cn(
          "bg-white box-border flex flex-col gap-4 items-center px-4 py-2 rounded-[0.3125rem] w-full",
          className
        )}
      >
        {/* Title */}
        <div className="flex flex-col gap-[0.5rem] w-full">
          <h2 className="font-montserrat font-semibold text-gray-900 text-xl leading-7 w-full">
            {t("passwordReset.reset.title")}
          </h2>
          <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 tracking-[0.3px] leading-5 w-full">
            {t("passwordReset.reset.subtitle", { email })}
          </p>
        </div>

        {/* Global Error Banner */}
        {error && (
          <div
            role="alert"
            className="w-full bg-red-100 border border-red-200 text-brand-red rounded-[0.3125rem] px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        {/* Form */}
        <form
          ref={ref}
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 w-full"
        >
          {/* Verification Code Input */}
          <div className="flex flex-col gap-1 w-full">
            <Input
              type="text"
              variant="anyw3ar"
              placeholder={t("passwordReset.reset.codePlaceholder")}
              disabled={isLoading}
              aria-label={t("passwordReset.reset.code")}
              aria-invalid={errors.code ? "true" : "false"}
              maxLength={6}
              className={cn(
                errors.code && "border-brand-red focus:ring-brand-red"
              )}
              {...register("code")}
            />
            {errors.code && (
              <span className="text-brand-red text-sm" role="alert">
                {t(errors.code.message as string)}
              </span>
            )}
          </div>

          {/* New Password Input with visibility toggle */}
          <div className="flex flex-col gap-1 w-full">
            <div className="relative w-full">
              <Input
                type={showPassword ? "text" : "password"}
                variant="anyw3ar"
                placeholder={t("passwordReset.reset.newPasswordPlaceholder")}
                disabled={isLoading}
                aria-label={t("passwordReset.reset.newPassword")}
                aria-invalid={errors.newPassword ? "true" : "false"}
                className={cn(
                  "pr-12",
                  errors.newPassword && "border-brand-red focus:ring-brand-red"
                )}
                {...register("newPassword")}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={
                  showPassword
                    ? t("passwordReset.reset.hidePassword")
                    : t("passwordReset.reset.showPassword")
                }
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOffIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <span className="text-brand-red text-sm" role="alert">
                {t(errors.newPassword.message as string)}
              </span>
            )}
            {/* Password Strength Indicator */}
            <PasswordStrengthIndicator password={newPassword} />
          </div>

          {/* Confirm Password Input with visibility toggle */}
          <div className="flex flex-col gap-1 w-full">
            <div className="relative w-full">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                variant="anyw3ar"
                placeholder={t(
                  "passwordReset.reset.confirmPasswordPlaceholder"
                )}
                disabled={isLoading}
                aria-label={t("passwordReset.reset.confirmPassword")}
                aria-invalid={errors.confirmPassword ? "true" : "false"}
                className={cn(
                  "pr-12",
                  errors.confirmPassword &&
                    "border-brand-red focus:ring-brand-red"
                )}
                {...register("confirmPassword")}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={
                  showConfirmPassword
                    ? t("passwordReset.reset.hidePassword")
                    : t("passwordReset.reset.showPassword")
                }
                disabled={isLoading}
              >
                {showConfirmPassword ? (
                  <EyeOffIcon size={16} />
                ) : (
                  <EyeIcon size={16} />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="text-brand-red text-sm" role="alert">
                {t(errors.confirmPassword.message as string)}
              </span>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary-red"
            size="primary-red"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading
              ? t("passwordReset.reset.submitting")
              : t("passwordReset.reset.submit")}
          </Button>

          {/* Resend Code Button */}
          <Button
            type="button"
            variant="primary-dark"
            size="primary-dark"
            onClick={onResendCode}
            disabled={!canResend || isLoading}
            className="w-full"
          >
            {canResend
              ? t("passwordReset.reset.resendCode")
              : t("passwordReset.reset.resendCodeCooldown", {
                  seconds: resendCooldown,
                })}
          </Button>
        </form>
      </div>
    );
  }
);

ResetPasswordForm.displayName = "ResetPasswordForm";
